import { useEffect, useRef } from "react";

import { AnalyticsContext, NavigationEvent } from "@salestrak/analytics-taxonomy";
import { trackEvent } from "#app/lib/analytics/analytics-session";
import { isTextEditingTarget } from "#app/lib/utils/typing-target";

/**
 * The one probe for "a modal dialog owns the keys": a page-level chord must
 * not act on something behind a modal, whether focus is inside the modal or
 * fell to `<body>`.
 */
export const MODAL_DIALOG_SELECTOR = '[role="dialog"][aria-modal="true"]';
export function isModalDialogOpen(): boolean {
  return document.querySelector(MODAL_DIALOG_SELECTOR) !== null;
}

/**
 * One chord. Exactly the named modifiers must be held: a chord that declares
 * `alt` fires on Alt alone, never on Alt+Shift, so two chords on one key
 * cannot both fire.
 *
 * `code` names the PHYSICAL key and is the right gate for a modifier chord:
 * Option is a compose modifier, so macOS reports `e.key` as "π" for ⌥P and a
 * key-based gate silently never fires. `key` names the PRINTED character and
 * is the right gate for a bare key: the letter the viewer sees is the letter
 * that works, on every layout. Compared case-insensitively (Caps Lock).
 */
export type HotkeyChord = {
  code?: string | readonly string[];
  key?: string;
  /** ⌘ on Mac, Ctrl elsewhere: fires on `metaKey || ctrlKey`. */
  mod?: boolean;
  alt?: boolean;
  shift?: boolean;
};

export type UseHotkeyOptions = HotkeyChord & {
  /** False detaches the chord entirely; the handler can never run. */
  enabled?: boolean;
  /** Fire inside an input, textarea, select or contenteditable. Default
   *  false: a bare key or an Alt chord is text there. A ⌘/Ctrl chord that is
   *  never typing (⌘K, ⌘/) sets it. */
  allowInEditor?: boolean;
  /** Fire while a modal dialog is open. Default false. */
  allowInModal?: boolean;
  /** The `navigation.use_hotkey` name for this chord. Defaults to the chord
   *  itself ("mod+k", "alt+shift+q"); set it where one chord means different
   *  actions on different screens. */
  analyticsId?: string;
  handler: (event: KeyboardEvent) => void;
};

/** A chord as a stable analytics name: "mod+alt+shift+<code or key>". */
export function chordName(chord: HotkeyChord): string {
  const code = typeof chord.code === "string" ? chord.code : chord.code?.[0];
  const parts = [
    chord.mod && "mod",
    chord.alt && "alt",
    chord.shift && "shift",
    (code ?? chord.key ?? "").toLowerCase(),
  ];
  return parts.filter(Boolean).join("+");
}

/** Whether `event` is exactly `chord`, modifiers included. */
export function matchesChord(event: KeyboardEvent, chord: HotkeyChord): boolean {
  if ((event.metaKey || event.ctrlKey) !== Boolean(chord.mod)) return false;
  if (event.altKey !== Boolean(chord.alt)) return false;
  if (event.shiftKey !== Boolean(chord.shift)) return false;
  if (chord.code !== undefined) {
    const codes = typeof chord.code === "string" ? [chord.code] : chord.code;
    if (!codes.includes(event.code)) return false;
  }
  if (chord.key !== undefined && event.key.toLowerCase() !== chord.key.toLowerCase()) {
    return false;
  }
  return chord.code !== undefined || chord.key !== undefined;
}

/**
 * Whether `event` is `chord` aimed at the page: not already handled, not
 * typing (unless allowed), not behind a modal (unless allowed).
 */
export function isPageHotkey(
  event: KeyboardEvent,
  chord: HotkeyChord,
  gates: { allowInEditor?: boolean; allowInModal?: boolean } = {},
): boolean {
  if (!matchesChord(event, chord)) return false;
  if (event.defaultPrevented) return false;
  if (!gates.allowInEditor && isTextEditingTarget(event.target)) return false;
  if (!gates.allowInModal && isModalDialogOpen()) return false;
  return true;
}

type Registration = { options: React.RefObject<UseHotkeyOptions> };

// ONE window listener for every page-level chord. Registrations are read in
// order and the first match wins, then the key is consumed.
const registrations = new Set<Registration>();

function handleKeyDown(event: KeyboardEvent): void {
  for (const registration of registrations) {
    const options = registration.options.current;
    if (options.enabled === false) continue;
    if (!isPageHotkey(event, options, options)) continue;
    event.preventDefault();
    options.handler(event);
    // A held key repeats keydown; count the press once.
    if (!event.repeat) {
      trackEvent({
        context: AnalyticsContext.Navigation,
        event: NavigationEvent.UseHotkey,
        data: { hotkey: options.analyticsId ?? chordName(options) },
      });
    }
    return;
  }
}

function register(registration: Registration): () => void {
  registrations.add(registration);
  if (registrations.size === 1) window.addEventListener("keydown", handleKeyDown);
  return () => {
    registrations.delete(registration);
    if (registrations.size === 0) {
      window.removeEventListener("keydown", handleKeyDown);
    }
  };
}

/**
 * Declares one page-level keyboard chord for as long as the caller is
 * mounted. Options are read through a ref, so a fresh handler each render
 * never re-registers. Chords that need document-order arbitration between
 * several mounted targets (the view search's `/`, the filter panel's F)
 * keep their own registries and read the gates above.
 */
export function useHotkey(options: UseHotkeyOptions): void {
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });
  useEffect(() => register({ options: optionsRef }), []);
}
